// Burrow Social v1 — API surface (live against Burrow Dev).
//
// Single import point for every social action a component needs.
// All queries go through `supabase` (configured via VITE_SUPABASE_URL).
// Schema is owned by docs/social-v1/migrations/001..012, applied to
// project `nlsqtuxvpffpqnwsrwed` (Burrow Dev).

import { supabase } from "../supabaseClient";
import { randomId } from "../randomId";
import type {
  AuthorRef,
  BorrowRecord,
  Card,
  Handle,
  KnowledgeChainLink,
  PublicProfile,
  TopInspirationAuthor,
  Visibility,
} from "../../types/social";

// Shared helper: build the canonical "Anonymous" author chip.
function anonymousAuthor(): AuthorRef {
  return {
    user_id: null,
    handle: null,
    display_name: "Anonymous",
    avatar_url: null,
    anonymous: true,
  };
}
import type { FollowListEntry } from "../../components/FollowList";
import type { BorrowerEntry } from "../../components/BorrowerList";

// ---- Profile loads ----

export interface ProfileBundle {
  profile: PublicProfile;
  followers: FollowListEntry[];
  following: FollowListEntry[];
  topInspiration: TopInspirationAuthor[];
}

export async function loadPublicProfile(handle: Handle): Promise<ProfileBundle | null> {
  const { data: row, error } = await supabase
    .from("profiles")
    .select("id, handle, display_name, avatar_url, hide_follower_list, hide_following_list, hide_top_inspiration")
    .eq("handle", handle)
    .maybeSingle();
  if (error || !row || !row.handle) return null;

  const [{ data: followerRows }, { data: followingRows }, currentUser] = await Promise.all([
    supabase.from("follows").select("follower_user_id").eq("followed_user_id", row.id),
    supabase.from("follows").select("followed_user_id").eq("follower_user_id", row.id),
    supabase.auth.getUser(),
  ]);

  const isOwnerView = currentUser.data.user?.id === row.id;

  // Card count (public for non-owner; all for owner), follow lists, and top
  // inspiration are independent — fetch them in one parallel wave instead of
  // four sequential round trips.
  const [{ count: cardCount }, followers, following, topInspiration, { data: activityRows }] = await Promise.all([
    // Anonymized cards are UNLINKED from their author (e.g. after the owner took
    // a public collection private) — they live on in the commons but must not be
    // counted on, or attributed to, this profile.
    isOwnerView
      ? supabase.from("thoughts").select("id", { count: "exact", head: true }).eq("owner_id", row.id).eq("anonymous", false)
      : supabase.from("thoughts").select("id", { count: "exact", head: true }).eq("owner_id", row.id).eq("visibility", "public").eq("anonymous", false),
    row.hide_follower_list
      ? Promise.resolve([] as FollowListEntry[])
      : resolveProfilesByIds((followerRows ?? []).map((r) => r.follower_user_id)),
    row.hide_following_list
      ? Promise.resolve([] as FollowListEntry[])
      : resolveProfilesByIds((followingRows ?? []).map((r) => r.followed_user_id)),
    row.hide_top_inspiration ? Promise.resolve([]) : loadTopInspirationInternal(row.id),
    // Activity totals (counts only — never the underlying private rows). Backed
    // by a SECURITY DEFINER function so the tallies are visible to any viewer.
    supabase.rpc("profile_activity_counts", { target_user_id: row.id }),
  ]);

  const activity = (activityRows as
    | { card_count: number; collection_count: number; edition_count: number; trail_count: number }[]
    | null)?.[0];

  const profile: PublicProfile = {
    user_id: row.id,
    handle: row.handle,
    display_name: row.display_name ?? row.handle,
    avatar_url: row.avatar_url,
    hide_follower_list: row.hide_follower_list,
    hide_following_list: row.hide_following_list,
    hide_top_inspiration: row.hide_top_inspiration,
    follower_count: followerRows?.length ?? 0,
    following_count: followingRows?.length ?? 0,
    public_card_count: cardCount ?? 0,
    total_card_count: activity?.card_count ?? cardCount ?? 0,
    total_collection_count: activity?.collection_count ?? 0,
    total_edition_count: activity?.edition_count ?? 0,
    total_trail_count: activity?.trail_count ?? 0,
    is_owner_view: isOwnerView,
  };

  return { profile, followers, following, topInspiration };
}

async function resolveProfilesByIds(ids: string[]): Promise<FollowListEntry[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from("profiles")
    .select("id, handle, display_name, avatar_url")
    .in("id", ids);
  if (error || !data) return [];
  return data
    .filter((p) => p.handle !== null)
    .map((p) => ({
      user_id: p.id,
      handle: p.handle as string,
      display_name: p.display_name ?? (p.handle as string),
      avatar_url: p.avatar_url,
    }));
}

/** Resolve the signed-in user's handle — what `/u/me` redirects to.
 *
 *  Pass `knownUserId` when the caller already has the session user (AuthProvider
 *  does). Without it this hits `/auth/v1/user` over the network first, and any
 *  hiccup there — offline, a token mid-refresh, a 401 — resolves to null, which
 *  `/u/me` renders as "Your handle is being set up." even for a user whose
 *  handle is fine. The id from context can't fail that way. */
export async function resolveCurrentUserHandle(knownUserId?: string): Promise<Handle | null> {
  let userId = knownUserId;
  if (!userId) {
    const { data: userResp } = await supabase.auth.getUser();
    userId = userResp.user?.id;
  }
  if (!userId) return null;
  const { data, error } = await supabase
    .from("profiles")
    .select("handle")
    .eq("id", userId)
    .maybeSingle();
  if (error) return null;
  return data?.handle ?? null;
}

// ---- Follow ----

export async function followUser(targetUserId: string): Promise<void> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me || me === targetUserId) return;
  await supabase.from("follows").insert({ follower_user_id: me, followed_user_id: targetUserId });
}

/** Whether the current user follows `targetUserId`. False when signed out. */
export async function isFollowingUser(targetUserId: string): Promise<boolean> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me || me === targetUserId) return false;
  const { count } = await supabase
    .from("follows")
    .select("follower_user_id", { count: "exact", head: true })
    .eq("follower_user_id", me)
    .eq("followed_user_id", targetUserId);
  return (count ?? 0) > 0;
}

export async function unfollowUser(targetUserId: string): Promise<void> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me) return;
  await supabase
    .from("follows")
    .delete()
    .eq("follower_user_id", me)
    .eq("followed_user_id", targetUserId);
}

export async function listFollowers(targetUserId: string, hidden: boolean): Promise<FollowListEntry[]> {
  if (hidden) return [];
  const { data } = await supabase
    .from("follows")
    .select("follower_user_id")
    .eq("followed_user_id", targetUserId);
  return resolveProfilesByIds((data ?? []).map((r) => r.follower_user_id));
}

export async function listFollowing(targetUserId: string, hidden: boolean): Promise<FollowListEntry[]> {
  if (hidden) return [];
  const { data } = await supabase
    .from("follows")
    .select("followed_user_id")
    .eq("follower_user_id", targetUserId);
  return resolveProfilesByIds((data ?? []).map((r) => r.followed_user_id));
}

// ---- Borrow ----

export interface BorrowParams {
  sourceCardId: string;
  inheritedColor: string | null;
}

export interface BorrowResult {
  borrow: BorrowRecord;
  newCardId: string;
  /** The row as inserted, so callers can put it straight into local state
   *  instead of waiting for the next full hydration. */
  newCard: {
    id: string;
    title: string | null;
    body: string;
    labels: string[];
    source_url: string | null;
    source_title: string | null;
    source_card_id: string | null;
    source_edition_id: string | null;
    created_at: string;
  };
}

export async function borrowCard({ sourceCardId, inheritedColor }: BorrowParams): Promise<BorrowResult> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me) throw new Error("Sign in to borrow.");

  // 1. Read source (RLS ensures it's public OR we own it)
  const { data: src, error: srcErr } = await supabase
    .from("thoughts")
    .select("id, title, body, labels, source_url, source_title, visibility, owner_id")
    .eq("id", sourceCardId)
    .maybeSingle();
  if (srcErr || !src) throw new Error("Source card not available.");
  if (src.visibility !== "public") throw new Error("Only public cards can be borrowed.");
  if (src.owner_id === me) throw new Error("You can't borrow your own card.");

  // 2. Resolve which published work the card is being borrowed *out of*, so the
  //    copy keeps a pointer back to it. `source_card_id` alone only records the
  //    card, which is why borrowed cards showed no source edition. The RPC is
  //    SECURITY DEFINER and prefers a public edition, then a public collection;
  //    a loose or private-destination card yields no edition, which is fine.
  //    Best-effort: a failure here must not cost the user their borrow.
  let sourceEditionId: string | null = null;
  try {
    const { data: dest } = await supabase.rpc("resolve_destination_work", { card_id: src.id });
    const work = Array.isArray(dest) ? dest[0] : dest;
    if (work?.kind === "edition" && work.work_id) sourceEditionId = work.work_id as string;
  } catch {
    // leave unlinked
  }

  // 3. Insert new card in borrower's library, private by default, with source pointer
  const { data: newCard, error: insErr } = await supabase
    .from("thoughts")
    .insert({
      owner_id: me,
      title: src.title ?? undefined,
      body: src.body,
      labels: src.labels ?? [],
      source_url: src.source_url ?? undefined,
      source_title: src.source_title ?? undefined,
      source_card_id: src.id,
      source_edition_id: sourceEditionId,
      visibility: "private",
      inherited_color: inheritedColor,
      is_public: false, // legacy flag kept in sync until cleanup migration
    })
    .select("id, title, body, labels, source_url, source_title, source_card_id, source_edition_id, created_at")
    .single();
  if (insErr || !newCard) throw new Error(insErr?.message ?? "Could not create borrowed card.");

  // 4. Record the borrow.
  const { data: borrow, error: brwErr } = await supabase
    .from("borrows")
    .insert({
      source_card_id: src.id,
      borrowed_card_id: newCard.id,
      borrower_user_id: me,
    })
    .select("*")
    .single();
  if (brwErr || !borrow) {
    // Steps 3 and 4 are separate round trips with no transaction around them.
    // Without this the copy survives as a card that claims a source but has no
    // borrow record — invisible to listBorrowers and the borrow counts, and
    // never cleaned up. Undo it so the borrow stays all-or-nothing.
    await supabase.from("thoughts").delete().eq("id", newCard.id).eq("owner_id", me);
    throw new Error(brwErr?.message ?? "Could not record borrow.");
  }

  // NO notification is written. Anywhere. PRD §4.6.
  return {
    borrow: {
      id: borrow.id,
      source_card_id: borrow.source_card_id,
      borrowed_card_id: borrow.borrowed_card_id,
      borrower_user_id: borrow.borrower_user_id,
      borrowed_at: borrow.borrowed_at,
    },
    newCardId: newCard.id,
    newCard: {
      id: newCard.id,
      title: newCard.title ?? null,
      body: newCard.body,
      labels: newCard.labels ?? [],
      source_url: newCard.source_url ?? null,
      source_title: newCard.source_title ?? null,
      source_card_id: newCard.source_card_id ?? null,
      source_edition_id: (newCard as { source_edition_id?: string | null }).source_edition_id ?? null,
      created_at: newCard.created_at,
    },
  };
}

export async function listBorrowers(sourceCardId: string): Promise<{ borrowers: BorrowerEntry[]; total: number }> {
  // Fetch all public borrows of this source card
  const { data: rows } = await supabase
    .from("borrows")
    .select("borrowed_card_id, borrower_user_id, thoughts!borrows_borrowed_card_id_fkey(id, visibility)")
    .eq("source_card_id", sourceCardId);

  type Row = { borrowed_card_id: string; borrower_user_id: string | null; thoughts: { id: string; visibility: Visibility } | null };
  const visible = ((rows ?? []) as unknown as Row[]).filter((r) => r.thoughts?.visibility === "public");
  if (visible.length === 0) return { borrowers: [], total: 0 };

  // Resolve borrower handles (skip null IDs — those render anonymous)
  const borrowerIds = Array.from(new Set(
    visible.filter((r) => r.borrower_user_id).map((r) => r.borrower_user_id!)
  ));
  let profileById = new Map<string, { id: string; handle: string | null; display_name: string | null; avatar_url: string | null }>();
  if (borrowerIds.length > 0) {
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, handle, display_name, avatar_url")
      .in("id", borrowerIds);
    profileById = new Map((profiles ?? []).map((p) => [p.id, p]));
  }

  // Resolve destination work for each borrowed card via resolve_destination_work RPC.
  // PRD §4.6: link goes to the public work the borrower placed the card into; if loose
  // or only in a private parent, link goes to the borrowed card itself.
  const destinations = await Promise.all(
    visible.map(async (r) => {
      const { data } = await supabase.rpc("resolve_destination_work", { card_id: r.borrowed_card_id });
      const d = (data as Array<{ kind: string; work_id: string | null; title: string | null; is_public: boolean }> | null)?.[0];
      let destination: BorrowerEntry["destination"];
      if (d?.kind === "edition" && d.work_id && d.title) {
        destination = { kind: "edition", id: d.work_id, title: d.title };
      } else if (d?.kind === "collection" && d.work_id && d.title) {
        destination = { kind: "collection", id: d.work_id, title: d.title };
      } else {
        // Loose, private parent, or no destination → link to the borrowed card itself
        destination = { kind: "loose_card", id: r.borrowed_card_id };
      }
      return [r.borrowed_card_id, destination] as const;
    })
  );
  const destinationByCardId = new Map(destinations);

  const borrowers: BorrowerEntry[] = visible.map((r) => {
    const p = r.borrower_user_id ? profileById.get(r.borrower_user_id) : null;
    const isAnonymous = !r.borrower_user_id || !p || !p.handle;
    return {
      user_id: r.borrower_user_id,
      handle: isAnonymous ? null : p!.handle,
      display_name: isAnonymous ? "Anonymous" : (p!.display_name ?? p!.handle ?? "Anonymous"),
      avatar_url: isAnonymous ? null : (p!.avatar_url ?? null),
      anonymous: isAnonymous,
      destination: destinationByCardId.get(r.borrowed_card_id) ?? { kind: "loose_card", id: r.borrowed_card_id },
    };
  });

  return { borrowers, total: borrowers.length };
}

// ---- Knowledge chain ----

export async function loadKnowledgeChain(cardId: string): Promise<KnowledgeChainLink[]> {
  const { data, error } = await supabase.rpc("knowledge_chain_for", { start_card_id: cardId });
  if (error || !data) return [];
  return (data as Array<{
    step: number;
    card_id: string;
    author_user_id: string | null;
    author_handle: string | null;
    author_display_name: string | null;
    destination_kind: string | null;
    destination_id: string | null;
    destination_title: string | null;
    destination_is_public: boolean | null;
  }>).map((row) => {
    const isAnonymous = !row.author_handle || !row.author_user_id;
    const author: AuthorRef = isAnonymous
      ? anonymousAuthor()
      : {
          user_id: row.author_user_id!,
          handle: row.author_handle!,
          display_name: row.author_display_name ?? row.author_handle!,
          avatar_url: null,
          anonymous: false,
        };
    return {
      card_id: row.card_id,
      author,
      destination_work: mapDestinationWork(row.destination_kind, row.destination_id, row.destination_title),
    };
  });
}

function mapDestinationWork(
  kind: string | null,
  id: string | null,
  title: string | null
): KnowledgeChainLink["destination_work"] {
  if (!kind) return null;
  if (kind === "private") return { kind: "private" };
  if (kind === "edition" && id && title) {
    return { kind: "edition", id, title, is_public: true };
  }
  if (kind === "collection" && id && title) {
    return { kind: "collection", id, title, is_public: true };
  }
  if (kind === "card" && id) {
    return { kind: "card", id, is_public: true };
  }
  return null;
}

// ---- Top inspiration ----

async function loadTopInspirationInternal(targetUserId: string): Promise<TopInspirationAuthor[]> {
  const { data, error } = await supabase.rpc("top_inspiration_for", { target_user_id: targetUserId });
  if (error || !data) return [];
  return (data as Array<{
    user_id: string;
    handle: string;
    display_name: string;
    avatar_url: string | null;
    borrow_count: number;
  }>).map((row) => ({
    user_id: row.user_id,
    handle: row.handle ?? "unknown",
    display_name: row.display_name ?? row.handle ?? "Unknown",
    avatar_url: row.avatar_url,
    borrow_count: Number(row.borrow_count ?? 0),
  }));
}

export async function loadTopInspiration(targetUserId: string, hidden: boolean): Promise<TopInspirationAuthor[]> {
  if (hidden) return [];
  return loadTopInspirationInternal(targetUserId);
}

// ---- Card visibility ----
// Per the social v1 rule: a card's visibility is DERIVED from its edition
// membership. It is NOT user-settable. To make a card public, publish a
// collection containing it as a public edition (the DB trigger handles the rest).
// This export is kept as a deprecated no-op for any lingering callsites; it does
// nothing and warns. Remove when no callers remain.
export async function setCardVisibility(_cardId: string, _visibility: Visibility): Promise<void> {
  console.warn("[social] setCardVisibility is a no-op — card visibility is derived from edition membership.");
}

// ---- Onboarding ----

export async function recordOnboardingThought(
  body: string,
  imageFile?: File | null
): Promise<{ id: string; image_path?: string }> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me) throw new Error("Not signed in.");
  const { data, error } = await supabase
    .from("thoughts")
    .insert({ owner_id: me, body, visibility: "private", is_public: false })
    .select("id")
    .single();
  if (error || !data) throw new Error(error?.message ?? "Could not save thought.");

  if (!imageFile) return { id: data.id };

  // Upload image and link via thoughts.image_path.
  // Path pattern matches existing BurrowStore convention: <user>/<thought>/<uuid>.<ext>.
  const ext = (imageFile.name.split(".").pop() ?? "jpg").toLowerCase();
  const safeExt = /^[a-z0-9]+$/.test(ext) ? ext : "jpg";
  const path = `${me}/${data.id}/${randomId()}.${safeExt}`;
  const { error: upErr } = await supabase.storage
    .from("thought-images")
    .upload(path, imageFile, {
      contentType: imageFile.type || "application/octet-stream",
      upsert: false,
    });
  if (upErr) return { id: data.id }; // thought saved, image upload best-effort

  await supabase.from("thoughts").update({ image_path: path }).eq("id", data.id);
  return { id: data.id, image_path: path };
}

export async function completeOnboarding(): Promise<void> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me) return;
  await supabase
    .from("profiles")
    .update({ onboarding_completed_at: new Date().toISOString() })
    .eq("id", me);
}

// ---- Profile settings ----

export interface ProfileHideFlags {
  hide_follower_list: boolean;
  hide_following_list: boolean;
  hide_top_inspiration: boolean;
}

export async function updateProfileHideFlags(flags: ProfileHideFlags): Promise<void> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me) return;
  await supabase.from("profiles").update(flags).eq("id", me);
}

/** Save (or clear, with null) the current user's profile picture URL. */
export async function updateProfileAvatar(avatarUrl: string | null): Promise<void> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me) return;
  await supabase.from("profiles").update({ avatar_url: avatarUrl }).eq("id", me);
}

// ---- Handle (public @username) ----

/** Mirrors the DB check constraint `profiles_handle_format`. */
export const HANDLE_REGEX = /^[a-z0-9][a-z0-9-]{0,29}$/;

/** Normalize free text into a candidate handle (lowercase, hyphen-joined). */
export function normalizeHandle(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30);
}

export type UpdateHandleResult =
  | { ok: true; handle: string }
  | { ok: false; reason: "invalid" | "taken" | "unauthenticated" | "error" };

/**
 * Change the current user's public @handle. Normalizes and validates the
 * candidate, then relies on the `profiles_handle_unique` index to reject
 * collisions (surfaced as `taken`). Handles are only stored on `profiles`,
 * so no denormalized copies need updating.
 */
export async function updateProfileHandle(raw: string): Promise<UpdateHandleResult> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me) return { ok: false, reason: "unauthenticated" };

  const handle = normalizeHandle(raw);
  if (!HANDLE_REGEX.test(handle)) return { ok: false, reason: "invalid" };

  const { error } = await supabase.from("profiles").update({ handle }).eq("id", me);
  if (error) {
    // 23505 = unique_violation → the handle is taken.
    if (error.code === "23505") return { ok: false, reason: "taken" };
    return { ok: false, reason: "error" };
  }
  return { ok: true, handle };
}

// ---- Borrow counts (used by community cards) ----

export async function loadBorrowCount(sourceCardId: string): Promise<number> {
  const { data } = await supabase
    .from("public_borrow_counts")
    .select("public_borrow_count")
    .eq("card_id", sourceCardId)
    .maybeSingle();
  return Number(data?.public_borrow_count ?? 0);
}

/** The signed-in user's own borrow of a source card, if any.
 *
 *  Needed because `public_borrow_counts` only counts borrows whose *copy* is
 *  public, and borrowed copies default to private — so borrowing a card leaves
 *  the count at 0 and `listBorrowers` empty, and the source card's sheet looked
 *  completely unchanged. The `borrows_select_public_or_own` policy always lets
 *  you read your own borrow rows, so this works regardless of copy visibility.
 *  Returns null when signed out or not borrowed. */
export async function loadMyBorrowOf(
  sourceCardId: string,
): Promise<{ borrowedCardId: string; borrowedAt: string } | null> {
  const { data: userResp } = await supabase.auth.getUser();
  const me = userResp.user?.id;
  if (!me) return null;
  const { data } = await supabase
    .from("borrows")
    .select("borrowed_card_id, borrowed_at")
    .eq("source_card_id", sourceCardId)
    .eq("borrower_user_id", me)
    .order("borrowed_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!data) return null;
  return { borrowedCardId: data.borrowed_card_id, borrowedAt: data.borrowed_at };
}

export async function loadBorrowCountsBatch(cardIds: string[]): Promise<Map<string, number>> {
  if (cardIds.length === 0) return new Map();
  const { data } = await supabase
    .from("public_borrow_counts")
    .select("card_id, public_borrow_count")
    .in("card_id", cardIds);
  const out = new Map<string, number>();
  for (const row of data ?? []) {
    if (row.card_id) out.set(row.card_id, Number(row.public_borrow_count ?? 0));
  }
  return out;
}

// ---- Public library load (cards for a profile) ----

export interface PublicCardForProfile {
  card: Card;
  author: AuthorRef;
  inherited_color: string | null;
  public_borrow_count: number;
  chain_depth: number;
  visibility: Visibility;
}

/** Background chain-attribution patch: card id → resolved origin. */
export type LibraryChainPatch = Map<string, { author: AuthorRef; depth: number }>;

export async function loadPublicLibrary(
  profile: PublicProfile,
  onChainsResolved?: (patch: LibraryChainPatch) => void
): Promise<PublicCardForProfile[]> {
  // Owner sees all; non-owner sees only public (also enforced by RLS).
  // Anonymized cards are excluded for everyone: making a public collection
  // private unlinks its cards from the author (they turn anonymous), so they
  // must drop off the author's profile even though they persist in the commons.
  const query = profile.is_owner_view
    ? supabase.from("thoughts").select("*").eq("owner_id", profile.user_id).eq("anonymous", false)
    : supabase.from("thoughts").select("*").eq("owner_id", profile.user_id).eq("visibility", "public").eq("anonymous", false);

  // Cap is a safety bound, not a product limit — it exists because borrowed,
  // unedited cards each resolve their knowledge chain via a per-card RPC below.
  // 500 covers every real library today; pagination is the proper fix if a
  // single user ever exceeds it.
  const { data: rows, error } = await query.order("created_at", { ascending: false }).limit(500);
  if (error || !rows || rows.length === 0) return [];

  const ids = rows.map((r) => r.id);
  const counts = await loadBorrowCountsBatch(ids);

  const ownerAuthor: AuthorRef = {
    user_id: profile.user_id,
    handle: profile.handle,
    display_name: profile.display_name,
    avatar_url: profile.avatar_url,
    anonymous: false,
  };

  // A borrowed card the owner hasn't edited is attributed to the ORIGINAL author
  // (the chain root), not the borrower. Once the owner edits its content (`edited`),
  // it's attributed to them — but the knowledge chain still shows where it came from.
  // The chain RPC already resolves upstream privacy/anonymity correctly.
  //
  // Each chain is its own RPC round-trip, so with `onChainsResolved` the cards
  // return immediately with provisional attribution (owner, depth 1) and the
  // caller gets patched once the chains land — the grid must not block on N
  // network calls. Without the callback, resolution stays blocking as before.
  const attributable = rows.filter(
    (r) => r.source_card_id && !(r as { edited?: boolean }).edited
  );
  const originByCardId: LibraryChainPatch = new Map();
  const resolveChains = () =>
    Promise.all(
      attributable.map(async (r) => {
        const chain = await loadKnowledgeChain(r.id); // root first, this card last
        if (chain.length > 0) {
          originByCardId.set(r.id, {
            author: chain[0].author,
            depth: Math.max(0, chain.length - 1),
          });
        }
      })
    );
  if (attributable.length > 0) {
    if (onChainsResolved) {
      void resolveChains().then(() => onChainsResolved(originByCardId));
    } else {
      await resolveChains();
    }
  }

  return rows.map((row) => {
    const c: Card = {
      id: row.id,
      title: row.title ?? undefined,
      body: row.body,
      source_url: row.source_url ?? undefined,
      source_title: row.source_title ?? undefined,
      connections: 0,
      labels: row.labels ?? [],
      created_at: row.created_at,
      image_path: row.image_path ?? undefined,
    };
    const origin = originByCardId.get(row.id);
    // Anonymous cards in someone's library can happen if the owner was deleted
    // and the card was re-surfaced via search-and-borrow loops. Defensive check.
    const author = (row as { anonymous?: boolean }).anonymous
      ? anonymousAuthor()
      : origin
        ? origin.author
        : ownerAuthor;
    return {
      card: c,
      author,
      inherited_color: row.inherited_color ?? null,
      public_borrow_count: counts.get(row.id) ?? 0,
      chain_depth: origin ? origin.depth : (row.source_card_id ? 1 : 0),
      visibility: ((row as { visibility?: Visibility }).visibility) ?? "public",
    };
  });
}

// ---- Public editions (for a profile) ----

export interface PublicEditionForProfile {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  cover_image_url: string | null;
}

/**
 * Published, public editions for a profile. Password-protected editions are
 * excluded for everyone — per PRD §5 they never appear on profiles, even the
 * owner's. RLS (`editions_select_public_or_own`) enforces the visitor side.
 */
export async function loadPublicEditions(userId: string): Promise<PublicEditionForProfile[]> {
  const { data, error } = await supabase
    .from("editions")
    .select("id, name, public_slug, created_at, cover_image_url")
    .eq("owner_id", userId)
    .eq("status", "published")
    .eq("visibility", "public")
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data
    .filter((e) => e.public_slug)
    .map((e) => ({
      id: e.id,
      name: e.name,
      slug: e.public_slug as string,
      created_at: e.created_at,
      cover_image_url: e.cover_image_url ?? null,
    }));
}

export interface PublicCollectionForProfile {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  cover_color: string | null;
  cover_image_url: string | null;
  card_count: number;
  view_count: number;
  updated_at: string;
}

/**
 * A user's public collections — the *living* counterpart to editions. Read
 * through the `collections_public` view (base `collections` is owner-only under
 * RLS); the view only ever exposes rows where `is_public` and a slug are set.
 */
export async function loadPublicCollections(userId: string): Promise<PublicCollectionForProfile[]> {
  const { data, error } = await supabase
    .from("collections_public")
    .select("id, name, public_slug, description, cover_color, cover_image_public_url, card_count, view_count, updated_at")
    .eq("owner_id", userId)
    .order("updated_at", { ascending: false });
  if (error || !data) return [];
  return data
    .filter((c) => c.public_slug)
    .map((c) => ({
      id: c.id,
      name: c.name,
      slug: c.public_slug as string,
      description: c.description ?? null,
      cover_color: c.cover_color ?? null,
      cover_image_url: (c as { cover_image_public_url?: string }).cover_image_public_url ?? null,
      card_count: c.card_count ?? 0,
      view_count: c.view_count ?? 0,
      updated_at: c.updated_at,
    }));
}

// ---- Community search ----

export interface CommunityHit {
  card: Card;
  author: AuthorRef;
  inherited_color: string | null;
  public_borrow_count: number;
  chain_depth: number;
}

export async function searchCommunity(query: string, limit = 24): Promise<CommunityHit[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const safe = q.replace(/[%_]/g, (m) => `\\${m}`);
  const pattern = `%${safe}%`;

  type ThoughtRow = {
    id: string;
    title: string | null;
    body: string;
    source_url: string | null;
    source_title: string | null;
    labels: string[] | null;
    created_at: string;
    image_path: string | null;
    inherited_color: string | null;
    source_card_id: string | null;
    owner_id: string | null;
    anonymous: boolean;
  };

  const { data: rowsRaw, error } = await supabase
    .from("thoughts")
    .select("*")
    .eq("visibility", "public")
    .or(`title.ilike.${pattern},body.ilike.${pattern}`)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error || !rowsRaw || rowsRaw.length === 0) return [];
  const rows = rowsRaw as ThoughtRow[];

  // Resolve attributed authors (skip nulls; those render anonymous).
  const ownerIds = Array.from(new Set(
    rows.filter((r) => r.owner_id && !r.anonymous).map((r) => r.owner_id!)
  ));
  let profileById = new Map<string, { id: string; handle: string | null; display_name: string | null; avatar_url: string | null }>();
  if (ownerIds.length > 0) {
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, handle, display_name, avatar_url")
      .in("id", ownerIds);
    profileById = new Map((profiles ?? []).map((p) => [p.id, p]));
  }

  const counts = await loadBorrowCountsBatch(rows.map((r) => r.id));

  return rows.map((row) => {
    const c: Card = {
      id: row.id,
      title: row.title ?? undefined,
      body: row.body,
      source_url: row.source_url ?? undefined,
      source_title: row.source_title ?? undefined,
      connections: 0,
      labels: row.labels ?? [],
      created_at: row.created_at,
      image_path: row.image_path ?? undefined,
    };
    let author: AuthorRef;
    if (row.anonymous || !row.owner_id) {
      author = anonymousAuthor();
    } else {
      const p = profileById.get(row.owner_id);
      if (!p || !p.handle) {
        author = anonymousAuthor();
      } else {
        author = {
          user_id: p.id,
          handle: p.handle,
          display_name: p.display_name ?? p.handle,
          avatar_url: p.avatar_url,
          anonymous: false,
        };
      }
    }
    return {
      card: c,
      author,
      inherited_color: row.inherited_color ?? null,
      public_borrow_count: counts.get(row.id) ?? 0,
      chain_depth: row.source_card_id ? 1 : 0,
    };
  });
}
